import type { FC, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";

import { ButtonAlpha } from "../../lib-canvas/ButtonAlpha";
import { type ButtonVariant } from "../../lib/Button";
import { useInputValue } from "../useInputValue";

const bgStyles = {
	"outlined--primary": "bg-primary",
	"outlined--primary--disabled": "bg-button-text-primary-disabled",
	"outlined--success": "bg-button-outlined-positive-focus",
	"outlined--success--disabled": "bg-button-text-positive-disabled",
	"outlined--error": "bg-button-outlined-negative-focus",
	"outlined--error--disabled": "bg-button-text-negative-disabled",
};

const borderStyles = {
	sm: "rounded",
	md: "rounded-md",
	lg: "rounded-lg",
};

const borderColor = {
	"outlined--disabled": "shadow-border-default",
	"outlined--primary":
		"shadow-border-dropshadow-default hover:shadow-border-dropshadow-hover focus:shadow-border-brand-focus active:shadow-border-brand-active",
	"outlined--success":
		"shadow-border-positive-light hover:shadow-border-positive-hover focus:shadow-border-positive-focus active:shadow-border-positive-active",
	"outlined--error":
		"shadow-border-negative-light hover:shadow-border-negative-hover focus:shadow-border-negative-focus active:shadow-border-negative-active",
};

const ringColor = {
	primary: "focus:ring focus:ring-default",
	success: "focus:ring focus:ring-positive",
	error: "focus:ring focus:ring-negative",
};

const textSize = {
	sm: "text-sm",
	md: "text-base",
	lg: "text-lg",
};

const textColor = {
	"outlined--disabled": "text-button-text-primary-disabled",
	"outlined--primary": "text-primary",
	"outlined--success": "text-primary",
	"outlined--error": "text-negative placeholder:text-negative-disabled",
};

const pStyles = {
	sm: "px-3 py-1.5",
	md: "px-3 py-2",
	lg: "px-4 py-3",
};

export interface InputTextProps {
	label?: string;
	placeholder?: string;
	defaultValue?: string;
	size?: "sm" | "md" | "lg";
	pushCondition?: "onChange" | "onSubmit";
	disabled?: boolean;
	pattern?: string;
	min?: number;
	step?: number;
	max?: number;
	minlength?: number;
	maxlength?: number;
	required?: boolean;
	value?: any;
	color?: any;
	useSetter?: any;
	children?: ReactNode;
	width?: "default" | "full";
}

export const InputTextAlpha: FC<InputTextProps> = ({
	label,
	placeholder,
	defaultValue,
	size = "sm",
	pushCondition = "onChange",
	disabled,
	pattern,
	minlength,
	maxlength,
	required,
	useSetter: propsUseSetter,
	...props
}) => {
	const { value: externalValue, ...rest } = props;
	const variant = "outlined";
	let color = rest.color === undefined ? "primary" : rest.color;

	//
	// Track "internal" value so we can call `useSetter` appropriately.
	//

	const { setInternalValue, pushValue, dirty, onPush } = useInputValue(
		pushCondition,
		defaultValue || "",
		externalValue
	);

	if (propsUseSetter) {
		propsUseSetter(pushValue, [pushValue]);
	}

	// useEffect(() => {
	//     if (propsUseSetter) {
	//         propsUseSetter(pushValue);
	//     }
	// }, [pushValue])

	//
	// Ref to track validity and prevent spurious event emission.
	//

	const ref = useRef<HTMLInputElement>(null);

	//
	// IMPORTANT: Prevent `InputEvent` so it doesn't trigger other cells inadvertently.
	//

	useEffect(() => {
		const helper = (e: any) => {
			//
			// IMPORTANT: Prevent `InputEvent` from propagating when the user
			// must click the submit button to fire the change off. If we do
			// not do this, the runtime picks up the event and registers it
			// as a cell change!
			//

			if (pushCondition === "onSubmit") {
				e.stopPropagation();
			}

			//
			// IMPORTANT: We can't register `onChange` on the `<input>`
			// because the `stopPropagation` above will have prevented it
			// before it gets to the `document`, which is where React places
			// its listeners. We must set it here instead.
			//

			setInternalValue(e.target.value);
		};

		//
		// IMPORTANT: Attach handler *MANUALLY*! React mounts event listeners to
		// `document`, and therefore can't intercept `InputEvent` before the
		// runtime picks them up and registers them as a change in the cell!
		//

		ref.current?.addEventListener("input", helper);
		return () => ref.current?.removeEventListener("input", helper);
	}, [ref.current, pushCondition]);

	//
	// Track validity of input.
	//

	const [_hasError, setHasError] = useState(false);
	useEffect(() => {
		setHasError(ref.current?.validity.valid === true);
	});

	const validityInfo = ref.current?.validity;
	if (validityInfo?.valid === false) {
		color = "error";
	}

	let errorCaption;
	if (validityInfo?.patternMismatch === true) {
		errorCaption = (
			<span>
				Input does not match pattern <span className="font-mono">/{pattern}/</span>
			</span>
		);
	} else if (validityInfo?.tooShort === true) {
		errorCaption = `Minimum input length is ${minlength}`;
	} else if (validityInfo?.tooLong === true) {
		errorCaption = `Maximum input length is ${maxlength}`;
	} else if (validityInfo?.valueMissing === true) {
		errorCaption = "Field is required";
	} else if (validityInfo?.rangeUnderflow === true) {
		errorCaption = `Minimum value is ${rest.min}`;
	} else if (validityInfo?.rangeOverflow === true) {
		errorCaption = `Maximum value is ${rest.max}`;
	} else if (validityInfo?.stepMismatch === true) {
		errorCaption = `Input must be a multiple of ${rest.step}`;
	} else if (validityInfo?.badInput === true) {
		errorCaption = "Input is invalid";
	}

	//
	// Render.
	//

	const colorOrDisabled = disabled ? "disabled" : color;
	const variantColor = `${variant}--${colorOrDisabled}`;

	const pointerEvents = disabled ? "pointer-events-none" : "";
	const bg = bgStyles[disabled ? `${variant}--${color}--disabled` : `${variant}--${color}`];
	const border = `${borderColor[variantColor]} ${borderStyles[size]}`;
	const ring = ringColor[colorOrDisabled];
	const text = `${textColor[variantColor]} ${textSize[size]}`;
	const p = pStyles[size];

	const error = errorCaption && (
		<span className={`text-negative ${textSize[size]}`}>{errorCaption}</span>
	);

	const input = (
		<input
			ref={ref}
			className={`w-72 focus:outline-none ${pointerEvents} ${bg} ${border} ${ring} ${text} ${p}`}
			id={label}
			disabled={disabled}
			placeholder={placeholder}
			defaultValue={defaultValue}
			pattern={pattern}
			minLength={minlength}
			maxLength={maxlength}
			required={required}
			{...rest}
		/>
	);

	const labelE = label && (
		<label
			className={`text-primary ${textSize[size]} mr-4 w-36 truncate font-medium`}
			htmlFor={label}
		>
			{label}
			{required && <span className="text-negative"> *</span>}
		</label>
	);

	if (pushCondition === "onChange") {
		return (
			<form className="flex flex-col space-y-2">
				<div className="flex flex-row items-center space-x-2">
					{labelE}
					{input}
				</div>
				{errorCaption && (
					<div className="flex flex-row items-center space-x-2">
						{label && <div className="invisible">{labelE}</div>}
						{error}
					</div>
				)}
			</form>
		);
	}

	let buttonVariant: ButtonVariant = "negative";
	if (color === "primary") {
		buttonVariant = "normal";
	} else if (color === "success") {
		buttonVariant = "positive";
	}

	return (
		<form
			className="flex flex-col space-y-2"
			onSubmit={(e) => {
				e.preventDefault();
				if (dirty) {
					onPush();
				}
			}}
		>
			<div className="flex flex-row items-center space-x-2">
				{labelE}
				{input}
				<ButtonAlpha
					label={"Submit"}
					variant={buttonVariant}
					size={size}
					onClick={() => {
						if (dirty) {
							onPush();
						}
					}}
					disabled={!dirty || color === "error"}
				/>
			</div>
			{errorCaption && (
				<div className="flex flex-row items-center space-x-2">
					{label && <div className="invisible">{labelE}</div>}
					{error}
				</div>
			)}
		</form>
	);
};
